/* eslint-disable @typescript-eslint/naming-convention */
import { canUseDOM } from '@ps/web-core-utils';
import type { CasinoMosaic } from '@ps/web-martech-cms-types';
import { applyPatch } from 'fast-json-patch';
import { produce } from 'immer';
import { get, isEmpty, range } from 'lodash';
import { filter, takeUntil, throttleTime } from 'rxjs/operators';

import {
  UPDATE_THROTTLE_TIME,
  favorites as baseFavorites,
  freeSpinBonus as baseFreeSpinBonus,
  extractPromoStream,
  getIcon,
  getLocalConfig,
  getMosaicProps,
  injectPromo,
  isFSB,
  preload as parentPreload,
  set,
  transformParams,
  userLoggedIn,
  userLoggedOut,
  userUpdates
} from '@swf/casino';
import type { CasinoGameService, FSBUpdate } from '@swf/casino';
import type { UseWidget } from '@swf/core/hooks/useWidget';
import { useWidget } from '@swf/core/hooks/useWidget';
import { areGamesVisibleByConfig, initialState } from '@swf/core/models/casino/utils';
import type {
  EffectParams,
  ExtractDispatchFromModel,
  ExtractSelectFromModel,
  Slicer,
  WidgetEffectParams,
  WidgetModelConfig
} from '@swf/core/store';
import { select } from '@swf/core/store';
import type {
  CasinoGame,
  GameLike,
  MosaicLocalConfig,
  StreamData,
  TrackedStreamData
} from '@swf/core/types/CasinoGame';
import { currencyChange, logout } from '@swf/core/utils/actions';
import { mosaicIndexRange } from '@swf/core/utils/casino';

export interface CasinoMosaicWidgetState {
  completed: boolean;
  currentPage: number;
  stream1: TrackedStreamData<GameLike>;
  stream2?: TrackedStreamData<GameLike>;
  stream3?: TrackedStreamData<GameLike>;
  realGames?: GameLike[];
  loading: boolean;
  localConfig?: MosaicLocalConfig;
}

export const createGameStream = (dataStream?: StreamData<CasinoGame>): Function => {
  const copy = dataStream?.games.slice(0) || [];
  return (packetSize: number) => copy.splice(0, packetSize);
};

const activeStreams = (widget: CasinoMosaic): string[] => {
  const active = [] as string[];

  mosaicIndexRange.forEach((i) => {
    const key = get(widget, [`column_${i}`, 'service']);
    if (!active.includes(key)) {
      active.push(key);
    }
  });
  return active;
};

export interface CasinoMosaicWidgetModel
  extends WidgetModelConfig<CasinoMosaic, CasinoMosaicWidgetState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

export const updateRealGames = (
  draft: CasinoMosaicWidgetState,
  params?: { localConfig: MosaicLocalConfig }
) => {
  if (!(draft.stream1.loading || draft.stream2?.loading || draft.stream3?.loading)) {
    const games = [] as GameLike[];

    let streamEmpty = false;
    const streams = {} as CasinoMosaicWidgetState;
    const localConfig = params?.localConfig || draft.localConfig;

    if (!localConfig) return draft;
    const { pages } = localConfig;

    localConfig.columnConfigs.forEach(
      (colConfig) =>
        (colConfig.stream =
          // @ts-expect-error - Type issue was not resolved prior to TS5 upgrade.
          streams[colConfig.serviceName] ||
          // @ts-expect-error - Type issue was not resolved prior to TS5 upgrade.
          (streams[colConfig.serviceName] = createGameStream(draft[colConfig.serviceName])))
    );

    range(0, pages).forEach((p) => {
      localConfig.columnConfigs.forEach((column) => {
        if (streamEmpty || !column.stream) {
          return;
        }
        const packet = column.stream(column.gamesPer);

        packet?.length === column.gamesPer ? games.push(...packet) : (streamEmpty = true);
      });
      if (p + 1 === pages && !streamEmpty) {
        draft.completed = true;
      }
    });
    draft.realGames = games;
  }
  return draft;
};

const reducers = {
  set,
  favorites: (state: CasinoMosaicWidgetState, { body }: { body: StreamData<CasinoGame> }) =>
    produce(state, (draft) => {
      draft.stream1 = baseFavorites(state.stream1, { body });
      if (state.stream2) {
        draft.stream2 = baseFavorites(state.stream2, { body });
      }
      if (state.stream3) {
        draft.stream3 = baseFavorites(state.stream3, { body });
      }
      updateRealGames(draft);
    }),
  freeSpinBonus: (state: CasinoMosaicWidgetState, { body }: { body: FSBUpdate[] }) =>
    produce(state, (draft) => {
      draft.stream1 = baseFreeSpinBonus(state.stream1, { body });
      if (state.stream2) {
        draft.stream2 = baseFreeSpinBonus(state.stream2, { body });
      }
      if (state.stream3) {
        draft.stream3 = baseFreeSpinBonus(state.stream3, { body });
      }
      updateRealGames(draft);
    }),
  updateRealGames: (state: CasinoMosaicWidgetState, params: { localConfig: MosaicLocalConfig }) =>
    produce(state, (draft) => updateRealGames(draft, params))
};

const nextPage = (actions: EffectParams['actions'], id: string) =>
  actions.pipe(filter(({ type }) => type === `${id}/loadMore`));

const effects = {
  async preload(payload: undefined, params: WidgetEffectParams<CasinoMosaic>) {
    const { dispatch, rootState, widget } = params;
    const widgetDispatch = dispatch[widget.uid];
    const widgetSelect: ExtractSelectFromModel<CasinoMosaicWidgetModel> = select[widget.uid];
    const localConfig = widgetSelect.getLocalConfig(rootState, widget);
    widgetDispatch.set({
      localConfig
    });
    await parentPreload(payload, params);
  },
  userLoggedIn,
  async clearState(payload: undefined, params: WidgetEffectParams<CasinoMosaic>) {
    const {
      widget: { uid },
      dispatch
    } = params;
    const widgetDispatch: ExtractDispatchFromModel<CasinoMosaicWidgetModel> = dispatch[uid];
    await widgetDispatch.set({
      stream1: { ...initialState },
      stream2: { ...initialState },
      stream3: { ...initialState }
    });
  },
  userLoggedOut,
  userUpdates,
  async fetch(payload: undefined, params: WidgetEffectParams<CasinoMosaic>) {
    const { actions, dispatch, select, request, requestStream, rootState, widget } = params;
    const widgetDispatch: ExtractDispatchFromModel<CasinoMosaicWidgetModel> = dispatch[widget.uid];
    const widgetSelect: ExtractSelectFromModel<CasinoMosaicWidgetModel> = select[widget.uid];
    const streamsToLoad = activeStreams(widget);
    const data = widgetSelect.get(rootState);

    const { locale, promotionStream } = extractPromoStream(widget, rootState);
    const onLogout = logout(actions) as NonNullable<ReturnType<typeof logout>>;

    if (
      !areGamesVisibleByConfig({
        hasState:
          isEmpty(data?.stream1.games) ||
          isEmpty(data?.stream2?.games) ||
          isEmpty(data?.stream3?.games),
        params,
        service: widget.game_service
      })
    ) {
      return;
    }

    const localConfig = widgetSelect.getLocalConfig(rootState, params.widget);
    const mosaicRendering = getMosaicProps(localConfig.layout, {});

    if (!canUseDOM) {
      const update = { currentPage: 0, loading: false, results: {} };
      widgetDispatch.set({ loading: true });

      await Promise.all(
        streamsToLoad.map(async (serviceName, index) => {
          // @ts-expect-error - Type issue was not resolved prior to TS5 upgrade.
          const item = widget[serviceName.replace('stream', 'stream_')];
          const option = {
            service: widget.game_service,
            body: transformParams(item.params)
          };

          const { body } = await request<TrackedStreamData<CasinoGame>>(option);
          // @ts-expect-error - Type issue was not resolved prior to TS5 upgrade.
          update[serviceName] = {
            ...injectPromo(
              body,
              index,
              locale,
              (gameIndex) => mosaicRendering[gameIndex % mosaicRendering.length].icon,
              promotionStream
            ),
            loading: false
          };
        })
      );
      update.loading = false;

      widgetDispatch.set(update);
      widgetDispatch.updateRealGames({ localConfig });
    } else {
      streamsToLoad.forEach((serviceName, index) => {
        // @ts-expect-error - Type issue was not resolved prior to TS5 upgrade.
        const item = widget[serviceName.replace('stream', 'stream_')];
        const option = {
          service: widget.game_service,
          body: {
            ...transformParams(item.params)
          }
        };

        requestStream<TrackedStreamData<CasinoGame>>(option, undefined)
          .pipe(
            throttleTime(UPDATE_THROTTLE_TIME, undefined, { leading: true, trailing: true }),
            takeUntil(currencyChange(actions) as typeof actions),
            takeUntil(onLogout)
          )
          .subscribe((update) => {
            widgetDispatch.set({
              [serviceName]: {
                ...injectPromo(
                  update,
                  index,
                  locale,
                  (gameIndex) => mosaicRendering[gameIndex % mosaicRendering.length].icon,
                  promotionStream
                ),
                loading: false
              }
            });
            widgetDispatch.updateRealGames({ localConfig });
          });
        widgetDispatch.set({ loading: false });
      });
    }
  },
  async loadMore(
    payload: undefined,
    {
      actions,
      rootState,
      request,
      requestStream,
      dispatch,
      widget
    }: WidgetEffectParams<CasinoMosaic>
  ) {
    const widgetDispatch: ExtractDispatchFromModel<CasinoMosaicWidgetModel> = dispatch[widget.uid];
    const widgetSelect: ExtractSelectFromModel<CasinoMosaicWidgetModel> = select[widget.uid];
    const localConfig = widgetSelect.getLocalConfig(rootState, widget);
    const data = widgetSelect.get(rootState);
    const streamsToLoad = activeStreams(widget);
    const onLogout = logout(actions) as NonNullable<ReturnType<typeof logout>>;

    const requestOptions = [] as { body: object; service: CasinoGameService }[];

    const results = await Promise.all(
      streamsToLoad.map((serviceName) => {
        // @ts-expect-error - Type issue was not resolved prior to TS5 upgrade.
        const item = widget[serviceName.replace('stream', 'stream_')];
        const options = {
          service: widget.game_service,
          body: { ...transformParams(item.params), patch: true, pg: data.currentPage + 1 }
        };
        requestOptions.push(options);
        return request(options);
      })
    );

    const patchedData = produce(data, (draft) => {
      let complete = false;
      results.forEach((result, index) => {
        const { body: patches } = result;
        // @ts-expect-error - Type issue was not resolved prior to TS5 upgrade.
        applyPatch(draft[streamsToLoad[index]], patches);
        complete = complete || !patches.length;
      });

      draft.currentPage = data.currentPage + 1;
      draft.completed = complete;
    });

    widgetDispatch.set(patchedData);

    streamsToLoad.forEach((active, index) => {
      // @ts-expect-error - Type issue was not resolved prior to TS5 upgrade.
      requestStream<CasinoMosaicWidgetState>(requestOptions[index], patchedData[active])
        .pipe(
          throttleTime(UPDATE_THROTTLE_TIME, undefined, { leading: true, trailing: true }),
          takeUntil(currencyChange(actions) as typeof actions),
          takeUntil(nextPage(actions, widget.uid)),
          takeUntil(onLogout)
        )
        .subscribe((update) => {
          // @ts-expect-error - Type issue was not resolved prior to TS5 upgrade.
          patchedData[active] = update;
          widgetDispatch.set({ ...patchedData, [active]: update });
        });
    });
    widgetDispatch.updateRealGames({ localConfig });
  }
};

const selectors = (slice: Slicer<CasinoMosaicWidgetState>) => ({
  get() {
    return slice;
  },
  getIcon,
  getLocalConfig,
  isFSB
});

export const mosaicWidgetModel: CasinoMosaicWidgetModel = {
  state: {
    stream1: { ...initialState, loading: true },
    stream2: { ...initialState },
    stream3: { ...initialState },
    completed: false,
    currentPage: 0,
    loading: false
  },
  reducers,
  effects,
  selectors
};

export const useMosaicWidgetModel: UseWidget<CasinoMosaic, CasinoMosaicWidgetModel> = useWidget;
